import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { getAllRecords } from "../api";
import SearchableSelect from "../components/SearchableSelect";
import TablePagination, { useTablePagination } from "../components/TablePagination";

export default function VendorManagementPage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]),
    [vehicles, setVehicles] = useState([]),
    [vendorDrivers, setVendorDrivers] = useState([]),
    [selected, setSelected] = useState(null),
    [vehicleId, setVehicleId] = useState(""),
    [show, setShow] = useState(false),
    [driver, setDriver] = useState({ fullName: "", mobileNo: "" }),
    [query, setQuery] = useState(""),
    [message, setMessage] = useState("");
  const load = async () => {
    try {
      const [v, rows, driverRows] = await Promise.all([
        api.get("/vehicles/vendors"),
        getAllRecords("/vehicles"),
        getAllRecords("/drivers"),
      ]);
      setVendors(v.data.data || []);
      setVehicles(rows);
      setVendorDrivers(driverRows);
    } catch {
      setMessage("Unable to load vendor data.");
    }
  };
  useEffect(() => {
    load();
  }, []);
  const vendorVehicles = vehicles.filter(
    (row) =>
      String(row.vendorId?._id || row.vendorId) === String(selected?._id),
  );
  const normalizedVehicleNumber = (value) =>
    String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const vehicleNumberMatches = (vehicleNumber, search) => {
    const candidate = normalizedVehicleNumber(vehicleNumber);
    const term = normalizedVehicleNumber(search);
    if (!term) return true;
    // Registration numbers are often entered without separators or the
    // leading zero in a state code (for example DL1… instead of DL01…).
    return (
      candidate.includes(term) ||
      candidate.replace(/0/g, "").includes(term.replace(/0/g, ""))
    );
  };
  const filteredVendors = vendors.filter((vendor) => {
    const vendorId = String(vendor._id);
    const vehicleNumbers = vehicles
      .filter((row) => String(row.vendorId?._id || row.vendorId) === vendorId)
      .map((row) => row.vehicleNumber);
    const driversForVendor = vendorDrivers
      .filter((row) => String(row.vendor?._id || row.vendor) === vendorId)
      .flatMap((row) => [row.fullName, row.mobileNo, row.driverId]);
    const externalDrivers = (vendor.externalDrivers || []).flatMap((row) => [
      row.fullName,
      row.mobileNo,
    ]);
    const searchable = [
      vendor.name,
      vendor.contactPerson,
      vendor.mobileNumber,
      ...vehicleNumbers,
      ...driversForVendor,
      ...externalDrivers,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const search = query.trim();
    return (
      searchable.includes(search.toLowerCase()) ||
      vehicleNumbers.some((vehicleNumber) =>
        vehicleNumberMatches(vehicleNumber, search),
      )
    );
  });
  const { page, setPage, pageRows } = useTablePagination(filteredVendors);
  const addDriver = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/vehicles/vendors/${selected._id}/drivers`, driver);
      setShow(false);
      setDriver({ fullName: "", mobileNo: "" });
      setMessage("Vendor driver added.");
      load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Unable to add vendor driver.");
    }
  };
  return (
    <div className="vendor-management-page">
      <div className="vehicle-heading">
        <div>
          <p>VENDOR MANAGEMENT</p>
          <h2>Vendor Management</h2>
          <span>Review vendor vehicles and external drivers.</span>
        </div>
        {selected && (
          <button className="add-vehicle-button" onClick={() => setShow(true)}>
            + Add Driver
          </button>
        )}
      </div>
      {message && <div className="vehicle-toast">{message}</div>}
      <section className="portal-module vendor-directory">
        <div className="vendor-directory-head"><div><p>COMPANY VENDORS</p><h3>Registered Vendors</h3></div><span>{vendors.length}</span></div>
        <div className="vendor-search"><input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="Search vendor, vehicle number, or driver" aria-label="Search vendors by vendor, vehicle, or driver" /><button type="button" onClick={() => { setQuery(""); setPage(1); }}>Clear</button></div>
        <div className="vendor-list-grid">
          {pageRows.map((vendor) => (
            <button
              className={`vendor-card ${selected?._id === vendor._id ? "active" : ""}`}
              key={vendor._id}
              onClick={() => navigate(`/staff/dashboard/vendor-management/${vendor._id}`)}
            >
              <div><b>{vendor.name}</b><span>{vendor.contactPerson || vendor.mobileNumber || "No contact details"}</span></div>
              <small>
                {
                  vehicles.filter(
                    (row) =>
                      String(row.vendorId?._id || row.vendorId) ===
                      String(vendor._id),
                  ).length
                } vehicles
              </small><em>Manage</em>
            </button>
          ))}
          {!filteredVendors.length && <p className="vendor-empty">No vendors found.</p>}
        </div>
        <TablePagination page={page} setPage={setPage} total={filteredVendors.length} label="Vendors" />
        {selected && (
          <div className="vendor-detail">
            <h3>{selected.name}</h3>
            <label>
              Vehicles
              <SearchableSelect
                value={vehicleId}
                onChange={setVehicleId}
                options={vendorVehicles.map((row) => ({
                  value: row._id,
                  label: row.vehicleNumber,
                }))}
                placeholder="Search this vendor's vehicles"
              />
            </label>
            <h3>Vendor Drivers</h3>
            {selected.externalDrivers?.length ? (
              <ul>
                {selected.externalDrivers.map((row) => (
                  <li key={row._id}>
                    {row.fullName} · {row.mobileNo}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No external drivers added.</p>
            )}
          </div>
        )}
      </section>
      {show && (
        <div className="fuel-modal-backdrop" onMouseDown={() => setShow(false)}>
          <form
            className="fuel-modal"
            onSubmit={addDriver}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header>
              <div>
                <p>VENDOR DRIVER</p>
                <h3>Add Driver</h3>
              </div>
              <button type="button" onClick={() => setShow(false)}>
                ×
              </button>
            </header>
            <div className="fuel-form-grid">
              <label>
                Driver Name
                <input
                  value={driver.fullName}
                  onChange={(e) =>
                    setDriver({ ...driver, fullName: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                Mobile Number
                <input
                  value={driver.mobileNo}
                  inputMode="numeric"
                  maxLength="10"
                  pattern="\d{10}"
                  onChange={(e) =>
                    setDriver({ ...driver, mobileNo: e.target.value })
                  }
                  required
                />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="cancel"
                onClick={() => setShow(false)}
              >
                Cancel
              </button>
              <button>Add Driver</button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
